import { type AbstractMesh, ImportMeshAsync, type Scene, TransformNode, Vector3 } from "@babylonjs/core";
import { create } from "@bufbuild/protobuf";
import { PlaceRequestSchema } from "../gen/livesoup/v1/realtime_pb";
import { client } from "../net/codec";
import { fileURL, type PlotInfo } from "../net/api";
import { $drafts, $panel, $pendingPlace, $placeMode, $placeSubmit, $plots, type PlaceSubmit, roleIn, toast } from "../state/stores";
import { draftBlocksPlacing } from "../world/plots";
import { canPlaceIn } from "../world/rules";
import { $confirmReady } from "../state/tools";
import type { WorldSession } from "../session";
import type { HUD } from "../hud/hud";
import type { EntityManager } from "./entities";
import type { BuiltWorld } from "./worldBuild";
import { hint } from "../state/touch";

export function inPlot(plot: { bounds: { min: [number, number]; max: [number, number] } }, x: number, z: number): boolean {
  const { min, max } = plot.bounds;
  return x >= min[0] && x < max[0] && z >= min[1] && z < max[1];
}

export function findPlot(plots: PlotInfo[], x: number, z: number): PlotInfo | null {
  return plots.find((p) => inPlot(p, x, z)) ?? null;
}

export class PlaceMode {
  private preview: TransformNode | null = null;
  private previewMeshes: AbstractMesh[] = [];
  private code: string | null = null;
  private yaw = 0;
  private valid = false;
  private target = Vector3.Zero();
  private requestSeq = 100;
  private obs: ReturnType<Scene["onBeforeRenderObservable"]["add"]> | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly session: WorldSession,
    private readonly world: BuiltWorld,
    private readonly entities: EntityManager,
    private readonly hud: HUD,
  ) {
    void this.world;
    void this.entities;
    $placeSubmit.listen((p) => { if (p) { this.send(p); $placeSubmit.set(null); } });
  }

  active(): boolean { return this.code !== null; }

  async start(code: string): Promise<void> {
    this.stop();
    this.code = code;
    $placeMode.set({ code });
    const root = new TransformNode("preview", this.scene);
    const result = await ImportMeshAsync(fileURL(code), this.scene, { pluginExtension: ".glb" });
    if (this.code !== code) { result.meshes.forEach((m) => m.dispose()); root.dispose(); return; }
    let minY = Infinity;
    for (const m of result.meshes) {
      m.isPickable = false;
      m.visibility = 0.55;
      minY = Math.min(minY, m.getBoundingInfo().boundingBox.minimumWorld.y);
    }
    for (const m of result.meshes.filter((m) => !m.parent)) { m.parent = root; if (Number.isFinite(minY)) m.position.y -= minY; }
    this.preview = root;
    this.previewMeshes = result.meshes;
    this.obs = this.scene.onBeforeRenderObservable.add(() => this.track());
  }

  private track(): void {
    if (!this.preview) return;
    const cam = this.session.player.camera;
    const ray = cam.getForwardRay(12);
    const hit = this.scene.pickWithRay(ray, (m) => m.metadata?.terrain === true || m.metadata?.plotFloor === true || m.name === "hall:floor" || m.name === "hall:porch");
    if (!hit?.hit || !hit.pickedPoint) { this.preview.setEnabled(false); this.valid = false; $confirmReady.set(false); this.hud.setPrompt("瞄準地面以放置"); return; }
    this.preview.setEnabled(true);
    this.target.copyFrom(hit.pickedPoint);
    this.preview.position.copyFrom(this.target);
    this.preview.rotation.y = (this.yaw * Math.PI) / 180;
    const plot = findPlot($plots.get(), this.target.x, this.target.z);
    const drafting = plot ? draftBlocksPlacing(plot, $drafts.get()) : null;
    this.valid = !!plot && !drafting && canPlaceIn(plot, roleIn(plot.id));
    $confirmReady.set(this.valid);
    for (const m of this.previewMeshes) {
      m.renderOverlay = !this.valid;
      m.overlayColor.set(1, 0.2, 0.2);
    }
    this.hud.setPrompt(this.valid
      ? hint(`左鍵放置到「${plot!.name}」 · 滾輪旋轉 · Esc 取消`, `點 ✓ 放置到「${plot!.name}」`)
      : drafting ?? (plot ? "你不能在這塊領地擺放" : "只能放在展示區或你的領地內"));
  }

  /** Turns the preview; the wheel and the ⟲ ⟳ buttons both come here (spec 2p §2.4). */
  rotate(deg: number): void {
    if (this.code) this.yaw = (((this.yaw + deg) % 360) + 360) % 360;
  }

  confirm(): void {
    if (!this.valid || !this.code) return;
    const plot = findPlot($plots.get(), this.target.x, this.target.z)!;
    const pending = { code: this.code, pos: { x: this.target.x, y: this.target.y, z: this.target.z }, yaw: this.yaw, plotId: plot.id, plotName: plot.name };
    this.stop();
    if (plot.kind === "estate") {
      $pendingPlace.set(pending);
      $panel.set("placeprops");
      return;
    }
    this.send({ ...pending, role: "", tags: [], componentsJson: "{}" });
  }

  private send(p: PlaceSubmit): void {
    this.session.ws.send(client({ case: "place", value: create(PlaceRequestSchema, {
      requestSeq: ++this.requestSeq, assetCode: p.code, pos: p.pos, yaw: p.yaw, scale: 1, plotId: p.plotId,
      role: p.role, tags: p.tags, componentsJson: p.componentsJson }) }));
    toast("已送出放置請求");
  }

  stop(): void {
    if (this.obs) { this.scene.onBeforeRenderObservable.remove(this.obs); this.obs = null; }
    this.previewMeshes.forEach((m) => m.dispose());
    this.previewMeshes = [];
    this.preview?.dispose();
    this.preview = null;
    this.code = null;
    $placeMode.set(null);
    $confirmReady.set(false);
    this.hud.setPrompt(null);
  }
}
